import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useEffect, useState } from "react";
import classnames from "classnames";
import Responsive from "./responsive";
import ResetState from "./resetstate";
import { GrigoraEditorContext } from "../editor/context";
import { INPUT_LIMITS } from "../input-limits";
function hasUnits(units) {
  return Array.isArray(units) && !!units.length;
}

function parseQuantityAndUnitFromRawValue(
  rawValue,
  allowedUnits,
  DEFAULT_UNIT,
) {
  let trimmedValue;
  let quantityToReturn;
  let isNegative;

  if (typeof rawValue !== "undefined" || rawValue === null) {
    trimmedValue = `${rawValue}`.trim();
    isNegative = trimmedValue.startsWith("-");
    if (isNegative) {
      trimmedValue = trimmedValue.slice(1);
    }

    const parsedQuantity = parseFloat(trimmedValue);
    quantityToReturn = !isFinite(parsedQuantity) ? undefined : parsedQuantity;
  }

  const unitMatch = trimmedValue?.match(/[\d.\-\+]*\s*(.*)/);

  const matchedUnit = unitMatch?.[1]?.toLowerCase();
  let unitToReturn;
  if (hasUnits(allowedUnits)) {
    const match = allowedUnits.find((item) => item.value === matchedUnit);
    unitToReturn = match?.value ? match?.value : DEFAULT_UNIT.value;
  } else {
    unitToReturn = DEFAULT_UNIT.value;
  }

  return [
    (isNegative ? "-" : "") +
      (!(quantityToReturn == null) ? quantityToReturn.toString() : ""),
    unitToReturn,
  ];
}

function UnitInput({
  value,
  onChange,
  label = "",
  className,
  resetValue,
  isResetVisible = true,
  auto,
  units = [
    {
      value: "px",
      label: "px",
    },
    {
      value: "%",
      label: "%",
    },
    {
      value: "rem",
      label: "rem",
    },
    {
      value: "em",
      label: "em",
    },
    {
      value: "vh",
      label: "vh",
    },
    {
      value: "vw",
      label: "vw",
    },
  ],
  unit = {
    value: "px",
    label: "px",
  },
  isResponsive,
  valueTablet,
  setValueTablet,
  resetValueTablet,
  valueMobile,
  setValueMobile,
  resetValueMobile,
  isNegativeValid = false,
  minLength,
  maxLength,
}) {
  const [active, setActive] = useState("desktop");
  const [currentUnit, setCurrentUnit] = useState("");
  const [currentUnitTablet, setCurrentUnitTablet] = useState("");
  const [currentUnitMobile, setCurrentUnitMobile] = useState("");

  const editorContext = useContext(GrigoraEditorContext);

  useEffect(() => {
    if (editorContext?.displaySize && isResponsive) {
      setActive(editorContext.displaySize);
    }
  }, [editorContext?.displaySize]);

  var defaultUnit;

  if (isResponsive) {
    if (active === "desktop") {
      if (currentUnit) {
        defaultUnit = {
          value: currentUnit,
          label: currentUnit,
        };
      } else {
        defaultUnit = unit;
      }
    } else if (active === "tablet") {
      if (currentUnitTablet) {
        defaultUnit = {
          value: currentUnitTablet,
          label: currentUnitTablet,
        };
      } else {
        defaultUnit = unit;
      }
    } else if (active === "mobile") {
      if (currentUnitMobile) {
        defaultUnit = {
          value: currentUnitMobile,
          label: currentUnitMobile,
        };
      } else {
        defaultUnit = unit;
      }
    }
  } else {
    if (currentUnit) {
      defaultUnit = {
        value: currentUnit,
        label: currentUnit,
      };
    } else {
      defaultUnit = unit;
    }
  }

  const [parsedQuantity, parsedUnit] = parseQuantityAndUnitFromRawValue(
    value,
    units,
    defaultUnit,
  );
  const [parsedQuantityTablet, parsedUnitTablet] =
    parseQuantityAndUnitFromRawValue(valueTablet, units, unit);
  const [parsedQuantityMobile, parsedUnitMobile] =
    parseQuantityAndUnitFromRawValue(valueMobile, units, unit);

  useEffect(() => {
    setCurrentUnit(parsedUnit);
    setCurrentUnitTablet(parsedUnitTablet);
    setCurrentUnitMobile(parsedUnitMobile);
  }, []);

  const handleNumberChange = (e) => {
    var { value: inputValue } = e.target;
    if (inputValue === "") {
      if (isResponsive) {
        if (active === "desktop") {
          onChange("");
        } else if (active === "tablet") {
          setValueTablet("");
        } else {
          setValueMobile("");
        }
      } else {
        onChange("");
      }
      return;
    }

    let isNegative;

    if (isNegativeValid && inputValue.startsWith("-")) {
      inputValue = inputValue.slice(1);
      isNegative = true;
    }

    const reg = /^\d+(\.\d+)?$/;
    if (
      reg.test(inputValue) ||
      inputValue === "" ||
      (isNegativeValid && inputValue === "-")
    ) {
      if (active === "desktop") {
        onChange((isNegative ? "-" : "") + inputValue + currentUnit);
      } else if (active === "tablet") {
        setValueTablet(
          (isNegative ? "-" : "") + inputValue + currentUnitTablet,
        );
      } else {
        setValueMobile(
          (isNegative ? "-" : "") + inputValue + currentUnitMobile,
        );
      }
    }
  };

  const handleUnitChange = (newUnit) => {
    if (active === "desktop") {
      setCurrentUnit(newUnit.target.value);
      onChange(parsedQuantity ? parsedQuantity + newUnit.target.value : "");
    } else if (active === "tablet") {
      setCurrentUnitTablet(newUnit.target.value);
      setValueTablet(
        parsedQuantityTablet ? parsedQuantityTablet + newUnit.target.value : "",
      );
    } else {
      setCurrentUnitMobile(newUnit.target.value);
      setValueMobile(
        parsedQuantityMobile ? parsedQuantityMobile + newUnit.target.value : "",
      );
    }
  };

  const handleBlur = () => {
    if (parsedQuantity === undefined || parsedQuantity === null) {
      return;
    }
    let valueTemp = parsedQuantity;
    if (
      parsedQuantity.charAt(parsedQuantity.length - 1) === "." ||
      parsedQuantity === "-"
    ) {
      valueTemp = parsedQuantity.slice(0, -1);
    }
    valueTemp = valueTemp.replace(/0*(\d+)/, "$1");
    onChange(valueTemp ? valueTemp + currentUnit : "");
  };

  const classes = classnames(
    "grigora-unit-input text-grigora-base gap-1 mb-2 flex items-center select-none justify-center ",
    className,
  );

  const handleOnKeyDown = (e) => {
    // Handle Up Down Arrow
    if (e.keyCode === 38 || e.keyCode === 40) {
      e.preventDefault();

      const currentVal =
        active === "desktop"
          ? parsedQuantity
            ? parsedQuantity
            : 0
          : active === "tablet"
          ? parsedQuantityTablet
            ? parsedQuantityTablet
            : 0
          : parsedQuantityMobile
          ? parsedQuantityMobile
          : 0;

      // Parse string to integer
      const currentValInt = parseInt(currentVal);

      // Handle up and down key
      const nextVal =
        e.keyCode === 38
          ? currentValInt + 1
          : !isNegativeValid && currentValInt === 0
          ? 0
          : currentValInt - 1;

      // Set value
      handleNumberChange({
        target: {
          value: nextVal.toString(),
        },
      });
    }
  };

  return (
    <div className={classes}>
      {label && (
        <div className="grigora-range-input__label flex flex-1 items-center">
          {label}
          {/* {isResponsive && (
            <div className="relative">
              <Responsive
                activeMode={active}
                onChangeActiveMode={(e) => {
                  setActive(e);
                }}
              />
            </div>
          )} */}
        </div>
      )}
      <ResetState
        active={
          isResetVisible &&
          (value != resetValue ||
            valueMobile != resetValueMobile ||
            valueTablet != resetValueTablet)
        }
        onClick={() => {
          onChange(resetValue);
          if (isResponsive) {
            // setActive("desktop");
            setValueTablet(resetValueTablet);
            setValueMobile(resetValueMobile);
          }
        }}
      />
      <div className="grigora-unit-input__field flex rounded-[6px] overflow-hidden border-[1px] border-gray-300 items-center justify-center duration-300 focus-within:border-gray-500">
        {auto && (
          <>
            <div
              onClick={() => {
                if (
                  (isResponsive
                    ? active === "desktop"
                      ? value
                      : active === "tablet"
                      ? valueTablet
                      : valueMobile
                    : value) === "auto"
                ) {
                  if (isResponsive) {
                    if (active === "desktop") {
                      onChange("");
                    } else if (active === "tablet") {
                      setValueTablet("");
                    } else {
                      setValueMobile("");
                    }
                  } else {
                    onChange("");
                  }
                } else {
                  if (isResponsive) {
                    if (active === "desktop") {
                      onChange("auto");
                    } else if (active === "tablet") {
                      setValueTablet("auto");
                    } else {
                      setValueMobile("auto");
                    }
                  } else {
                    onChange("auto");
                  }
                }
              }}
              className={`flex gap-[5px] items-center justify-center h-6 p-2 w-14 hover:bg-gray-200 ${
                (isResponsive
                  ? active === "desktop"
                    ? value
                    : active === "tablet"
                    ? valueTablet
                    : valueMobile
                  : value) === "auto"
                  ? "bg-editor-bg-light"
                  : "bg-editor-input-selected"
              }`}
            >
              Auto
              {(isResponsive
                ? active === "desktop"
                  ? value
                  : active === "tablet"
                  ? valueTablet
                  : valueMobile
                : value) === "auto" && (
                <FontAwesomeIcon icon={faCheck} size="sm" />
              )}
            </div>
          </>
        )}
        {(isResponsive
          ? active === "desktop"
            ? value
            : active === "tablet"
            ? valueTablet
            : valueMobile
          : value) !== "auto" && (
          <>
            <input
              type="text"
              value={
                active === "desktop"
                  ? parsedQuantity
                    ? parsedQuantity
                    : ""
                  : active === "tablet"
                  ? parsedQuantityTablet
                    ? parsedQuantityTablet
                    : ""
                  : parsedQuantityMobile
                  ? parsedQuantityMobile
                  : ""
              }
              onChange={handleNumberChange}
              onBlur={handleBlur}
              onKeyDown={handleOnKeyDown}
              minLength={
                minLength ? minLength : INPUT_LIMITS["unit-input"].minlength
              }
              maxLength={
                maxLength ? maxLength : INPUT_LIMITS["unit-input"].maxlength
              }
              className="h-6 px-1 pr-0 p-2 w-10 bg-editor-input-selected focus-visible:outline-none"
            />
            <select
              value={
                active === "desktop"
                  ? currentUnit
                  : active === "tablet"
                  ? currentUnitTablet
                  : active === "mobile"
                  ? currentUnitMobile
                  : ""
              }
              onChange={handleUnitChange}
              className="h-6 px-1 duration-300 focus-visible:border-gray-500 focus-visible:outline-none bg-editor-input-selected appearance-none hover:bg-gray-200"
            >
              {units.map(function (item, i) {
                return (
                  <option value={item.value} key={item.value}>
                    {item.label}
                  </option>
                );
              })}
            </select>
          </>
        )}
      </div>
    </div>
  );
}

export default UnitInput;
